import type { ClientErrorReportLabels } from "./clientErrorReportText";

export type ClientErrorSourceKind = "ApiError" | "ClientCrash" | "RouteError" | "Unhandled" | "ToastError";

export type ClientErrorPostInput = {
  componentStack?: string | null;
  context?: Record<string, boolean | number | string | null | undefined>;
  errorId: string;
  message: string;
  source?: string | null;
  sourceKind?: ClientErrorSourceKind;
  stack?: string | null;
  timestamp?: string | null;
  url?: string | null;
  userAgent?: string | null;
};

export type ClientErrorReporter = (payload: ClientErrorPostInput) => void;

export type ClientErrorRecoveryLabels = ClientErrorReportLabels & {
  anErrorOccurred: string;
  backToDashboard: string;
  close: string;
  copyFailed: string;
  copyErrorReport: string;
  developerInformation: string;
  errorReportCopied: string;
  reloadPage: string;
};

const maxSerializedLength = 1000;
const EMPTY_JSON_OBJECT = "{}";

/**
 * Eigene Felder eines geworfenen Objekts als JSON. `String(obj)` liefert nur „[object Object]“ —
 * damit wären im Protokoll weder Ursache noch Ort erkennbar.
 */
function serializeThrownObject(value: object): string | null {
  try {
    const plain: Record<string, unknown> = {};
    for (const key of Object.getOwnPropertyNames(value)) {
      const entry = (value as Record<string, unknown>)[key];
      plain[key] = typeof entry === "function" ? "[function]" : entry;
    }
    const json = JSON.stringify(plain);
    if (!json || json === EMPTY_JSON_OBJECT) return null;
    return json.length > maxSerializedLength ? `${json.slice(0, maxSerializedLength)}…` : json;
  } catch {
    // Zirkuläre Referenzen oder werfende Getter: dann bleibt nur die Standarddarstellung.
    return null;
  }
}

/**
 * Verbindliche Umwandlung eines geworfenen Werts in einen berichtsfähigen Error. Wird ein Objekt
 * geworfen (kein Error), bleiben dessen Felder in der Meldung und ein vorhandener Stack erhalten.
 */
export function toReportableError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;
  if (typeof thrown === "string") return new Error(thrown);
  if (thrown != null && typeof thrown === "object") {
    const error = new Error(serializeThrownObject(thrown) ?? String(thrown));
    const thrownStack = (thrown as { stack?: unknown }).stack;
    if (typeof thrownStack === "string" && thrownStack.trim() !== "") error.stack = thrownStack;
    return error;
  }
  return new Error(String(thrown ?? "Unknown error"));
}

/** `error.cause` als eine Zeile, z. B. für Bericht und Protokoll. */
export function formatErrorCause(error: Error | null | undefined): string | null {
  const cause = (error as (Error & { cause?: unknown }) | null | undefined)?.cause;
  if (cause == null) return null;
  if (cause instanceof Error) return [cause.name, cause.message].filter(Boolean).join(": ") || String(cause);
  if (typeof cause === "string") return cause;
  try {
    return JSON.stringify(cause);
  } catch {
    return String(cause);
  }
}

/** Request-ID eines API-Fehlers, sofern der Produkt-Client sie am Fehler führt. */
export function readErrorRequestId(error: Error | null | undefined): string | null {
  const requestId = (error as (Error & { requestId?: unknown }) | null | undefined)?.requestId;
  return typeof requestId === "string" || typeof requestId === "number" ? String(requestId) : null;
}

/** Stack für die Meldung ans Backend; die Ursache steht als `Cause:`-Zeile darunter. */
export function buildReportedStack(error: Error): string | null {
  const cause = formatErrorCause(error);
  const parts = [error.stack ?? "", cause ? `Cause: ${cause}` : ""].filter(Boolean);
  return parts.length > 0 ? parts.join("\n\n") : null;
}
